import React, { useEffect, useState, useRef } from "react";
import {
  toast,
  useDispatch,
  useForm,
  useSelector,
} from "../../Components/Imports/import";
import Modal from "react-modal";
import CloseIcon from "../../Assets/Svg/close-icon.svg";
import "./FinalCallPopup.css";
import {
  fetchFinalCallVendorsAction,
  finalCallAction,
} from "../../Store/actions/jobManagementAction";
import areIcon from "../../Assets/Svg/areyousure-icon.svg";
import successIcon from "../../Assets/Svg/success-icon.svg";

const FinalCallPopup = ({
  FinalCallIsOpen,
  FinalCallCloseModal,
  FinalCallFormTitle,
  rowData,
}) => {
  const [finalCallEndDate, setFinalCallEndDate] = useState("");
  const [finalCallVendors, setFinalCallVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dateError, setDateError] = useState("");
  const [vendorInputValue, setVendorInputValue] = useState("");
  const [vendorInputError, setVendorInputError] = useState("");
  const [showAreYouSurePopup, setShowAreYouSurePopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  const { loading, error, finalCallData, finalCallVendorsData } = useSelector(
    (state) => state.getJobManagementInfo,
  );
  const dispatch = useDispatch();
  const { handleSubmit } = useForm();

  useEffect(() => {
    if (rowData && rowData?.JobId) {
      dispatch(fetchFinalCallVendorsAction(rowData?.JobId));
    }
  }, [rowData]);

  useEffect(() => {
    if (finalCallVendorsData) {
      setFinalCallVendors(finalCallVendorsData?.data);
    }
  }, [finalCallVendorsData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const toggleVendor = (vendorId) => {
    setVendorInputError("");
    setSelectedVendors((prev) => {
      const updated = prev.includes(vendorId)
        ? prev.filter((v) => v !== vendorId)
        : [...prev, vendorId];
      const codes = finalCallVendors
        ?.filter((v) => updated.includes(v.VendorId))
        .map((v) => v.vendor_code);
      setVendorInputValue(codes?.join(",") || "");
      return updated;
    });
  };

  const getSelectedDisplay = () => {
    if (selectedVendors.length === 0) return "";
    const selectedCodes = finalCallVendors
      ?.filter((v) => selectedVendors.includes(v.VendorId))
      .map((v) => v.vendor_code);
    return selectedCodes?.join(",") || "";
  };

  const validateInput = (value) => {
    if (!value.trim()) {
      setSelectedVendors([]);
      setVendorInputError("");
      return;
    }

    const inputValues = value
      .split(",")
      .map((val) => val.trim())
      .filter((val) => val !== "");

    const invalidValues = [];
    const validIds = [];

    inputValues.forEach((val) => {
      const vendorByCode = finalCallVendors?.find(
        (v) => v.vendor_code?.toLowerCase() === val.toLowerCase(),
      );
      if (vendorByCode) {
        if (!validIds.includes(vendorByCode.VendorId)) {
          validIds.push(vendorByCode.VendorId);
        }
        return;
      }
      const numId = Number(val);
      if (!isNaN(numId)) {
        const vendorById = finalCallVendors?.find((v) => v.VendorId === numId);
        if (vendorById) {
          if (!validIds.includes(vendorById.VendorId)) {
            validIds.push(vendorById.VendorId);
          }
          return;
        }
      }

      invalidValues.push(val);
    });

    setSelectedVendors(validIds);

    if (invalidValues.length > 0) {
      setVendorInputError(
        `Invalid Vendor(s): ${invalidValues.join(", ")}. Please enter correct vendor code or ID.`,
      );
    } else {
      setVendorInputError("");
    }
  };

  const handleVendorInputChange = (e) => {
    const value = e.target.value;
    setVendorInputValue(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!value.endsWith(",")) {
      debounceTimer.current = setTimeout(() => {
        validateInput(value);
      }, 1000);
    }
  };

  const handleVendorInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      validateInput(vendorInputValue);
    }
  };

  const handleVendorInputFocus = () => {
    setDropdownOpen(true);
  };

  const handleVendorInputBlur = () => {
  if (debounceTimer.current) {
    clearTimeout(debounceTimer.current);
  }
};

  const onSubmit = async (data, e) => {
    e.preventDefault();
    let hasError = false;
    if (!finalCallEndDate) {
      setDateError("Please select a Final Price End Date");
      hasError = true;
    }
    if (selectedVendors.length === 0) {
      setVendorInputError("Please select at least one vendor");
      hasError = true;
    }
    if (hasError) {
      toast.error("Please fill in all required fields", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }
    setDateError("");
    setVendorInputError("");
    setShowAreYouSurePopup(true);
  };

  const handleConfirmSubmit = async () => {
    const payload = {
      jobId: rowData?.JobId,
      finalCallEndDate,
      finalCallVendorIds: selectedVendors.join(","),
    };
    try {
      const result = await dispatch(finalCallAction(payload));
      if (result === true) {
        setShowAreYouSurePopup(false);
        setShowSuccessPopup(true);
      } else {
        setShowAreYouSurePopup(false);
      }
    } catch (err) {
      setShowAreYouSurePopup(false);
    }
  };

  const closeAreYouSurePopup = () => {
    setShowAreYouSurePopup(false);
  };

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
    onClickClose();
  };

  const onClickClose = () => {
    setFinalCallEndDate("");
    setSelectedVendors([]);
    setDateError("");
    setDropdownOpen(false);
    setVendorInputValue("");
    setVendorInputError("");
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    FinalCallCloseModal();
  };

  const handleDateChange = (e) => {
    setFinalCallEndDate(e.target.value);
    if (dateError) setDateError("");
  };

  const isSubmitDisabled =
    !finalCallEndDate ||
    selectedVendors.length === 0 ||
    vendorInputError !== "";

  return (
    <>
      <div className="d-flex justify-content-center">
        <Modal
          isOpen={FinalCallIsOpen}
          onRequestClose={onClickClose}
          className="create-final-call-modal"
          overlayClassName="create-modal-overlay"
          shouldCloseOnOverlayClick={false}
        >
          <div className="create-user-title">
            <h2>{FinalCallFormTitle}</h2>
            <img
              src={CloseIcon}
              alt="close"
              onClick={onClickClose}
              className="final-call-close-icon"
            />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="create-user-form">
            <div className="final-call-field-wrapper fc-date-wrapper">
              <label htmlFor="finalCallEndDate" className="final-call-label">
                End Date
              </label>
              <input
                type="date"
                id="finalCallEndDate"
                value={finalCallEndDate}
                onChange={handleDateChange}
                className="final-call-date-input"
                min={new Date().toISOString().split("T")[0]}
              />
              {dateError && <p className="final-call-error-m">{dateError}</p>}
            </div>

            <div
              className="final-call-field-wrapper fc-vendor-wrapper"
              ref={dropdownRef}
            >
              <label className="final-call-label">Vendor code</label>
              <div
                className="fc-vendor-select"
                onClick={() => setDropdownOpen(true)}
              >
                <input
                  type="text"
                  className="fc-vendor-input"
                  placeholder="Enter vendor code"
                  value={vendorInputValue}
                  onChange={handleVendorInputChange}
                  onBlur={handleVendorInputBlur}
                  onKeyDown={handleVendorInputKeyDown}
                  onFocus={handleVendorInputFocus}
                />
                <span
                  className="fc-vendor-arrow"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen((prev) => !prev);
                  }}
                >
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path
                      d="M1 1.5L6 6.5L11 1.5"
                      stroke="#103a5d"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              {vendorInputError && (
                <p className="final-call-error-m">{vendorInputError}</p>
              )}
              {dropdownOpen && (
                <div className="fc-vendor-dropdown">
                  {finalCallVendors && finalCallVendors.length > 0 ? (
                    finalCallVendors.map((vendor, index) => (
                      <label key={index} className="fc-vendor-option">
                        <input
                          type="checkbox"
                          checked={selectedVendors.includes(vendor.VendorId)}
                          onChange={() => toggleVendor(vendor.VendorId)}
                          className="fc-vendor-checkbox"
                        />
                        <span className="fc-vendor-code">
                          {vendor.vendor_code}
                        </span>
                        <span className="fc-vendor-name">
                          {vendor.company_name_eng}
                        </span>
                      </label>
                    ))
                  ) : (
                    <div className="fc-vendor-option fc-no-vendors">
                      No vendors available
                    </div>
                  )}
                </div>
              )}
            </div>

            {dropdownOpen && <div className="fc-dropdown-spacer"></div>}

            <div className="create-btns-div">
              <button
                type="submit"
                className="um-submit-btn"
                disabled={isSubmitDisabled}
                style={{
                  opacity: isSubmitDisabled ? 0.5 : 1,
                  cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                }}
              >
                Submit
              </button>
            </div>
          </form>
        </Modal>
      </div>

      <Modal
        isOpen={showAreYouSurePopup}
        onRequestClose={closeAreYouSurePopup}
        className="create-user-modal"
        overlayClassName="create-modal-overlay"
        shouldCloseOnOverlayClick={false}
      >
        <div className="close-icon-div">
          <img
            src={CloseIcon}
            alt=""
            onClick={closeAreYouSurePopup}
            style={{ cursor: "pointer" }}
          />
        </div>
        <center>
          <img src={areIcon} alt="" />
          <h2 className="pt-2 popup-title">Are you sure!</h2>
          <p className="pt-2 popup-text">You want to set this Final Price?</p>
          <div className="pt-4">
            <button onClick={closeAreYouSurePopup} className="um-create-btn">
              Cancel
            </button>
            <button
              className="um-submit-btn mx-2"
              onClick={handleConfirmSubmit}
            >
              Confirm
            </button>
          </div>
        </center>
      </Modal>

      <Modal
        isOpen={showSuccessPopup}
        onRequestClose={closeSuccessPopup}
        className="create-user-modal"
        overlayClassName="create-modal-overlay"
        shouldCloseOnOverlayClick={false}
      >
        <div className="close-icon-div">
          <img
            src={CloseIcon}
            alt=""
            onClick={closeSuccessPopup}
            style={{ cursor: "pointer" }}
          />
        </div>
        <center>
          <img src={successIcon} alt="" />
          <h2 className="pt-2 popup-title">Done!</h2>
          <p className="pt-2 popup-text">Final Price set successfully</p>
          <div className="pt-3">
            <button className="um-submit-btn mx-2" onClick={closeSuccessPopup}>
              Ok
            </button>
          </div>
        </center>
      </Modal>
    </>
  );
};

export default FinalCallPopup;
